/**
 * ExpiryItems.tsx  ("All Documents" page)
 *
 * Full document list with clickable summary cards, search, bulk actions
 * and per-row context menu.
 *
 * All shared data-fetching, modal state and action handlers live in
 * `useDocumentPage`. ExpiryItems adds only what is unique to this page:
 *   - Card-filter state (same pattern as Dashboard)
 *   - ?new=1 URL param to auto-open the "Add Document" modal
 */

import React, { useState, useMemo, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";

import Header from "../components/Header";
import SummaryCard from "../components/SummaryCard";
import SearchBar from "../components/SearchBar";
import DataTable from "../components/DataTable";
import BulkActionBar from "../components/BulkActionBar";
import ItemModal from "../components/ItemModal";
import ImportModal from "../components/ImportModal";
import SettingsModal from "../components/SettingsModal";
import HistoryDrawer from "../components/HistoryDrawer";
import NewUserWelcome from "../components/NewUserWelcome";

import { useDocumentPage } from "../hooks/useDocumentPage";
import { useExpiryColumns } from "../hooks/useExpiryColumns";
import { useAuth, useRole } from "../hooks/useAuth";

import {
  buildSummaryCards,
  filterByCard,
  filterBySearch,
} from "../utils/expiryUtils";
import { CARD_FILTER_VALUES } from "../constants/expiry";
import type { CardFilter } from "../types";

// ─── Component ────────────────────────────────────────────────────────────────

const ExpiryItems: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { canEdit } = useRole();

  const {
    items,
    isLoading,
    isModalOpen,
    editingItem,
    modalRenewalMode,
    modalFocusExpiry,
    closeModal,
    drawerItem,
    openDrawer,
    closeDrawer,
    selectedRows,
    handleRowSelect,
    handleClearSelection,
    searchQuery,
    setSearchQuery,
    handleCreate,
    handleEdit,
    handleAddFields,
    handleRenewItem,
    handleSendReminder,
    handleSubmit,
    handleRowAction,
    handleImport,
    handleBulkRenew,
    handleBulkEditExpiry,
    handleBulkAssignOwner,
    handleBulkChangeCategory,
    handleBulkSnooze,
    handleBulkArchive,
    handleBulkExport,
  } = useDocumentPage();

  // ── Page-specific modal state ──────────────────────────────────────────────
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);
  // Holds an item ID from ?open= until items finish loading
  const [pendingOpenId, setPendingOpenId] = useState<string | null>(null);

  // ── Welcome / sample-data dismiss state ───────────────────────────────────
  // Persisted in localStorage so the welcome screen stays hidden after refresh.
  const WELCOME_KEY = "sentrilog_welcome_dismissed_v1";
  const [welcomeDismissed, setWelcomeDismissed] = useState<boolean>(
    () => localStorage.getItem(WELCOME_KEY) === "true"
  );
  const dismissWelcome = () => {
    localStorage.setItem(WELCOME_KEY, "true");
    setWelcomeDismissed(true);
  };

  // ── Card filter state (same pattern as Dashboard) ─────────────────────────
  const [activeFilter, setActiveFilter] = useState<CardFilter>(null);

  // ── Auto-open modals / apply filters from URL params ─────────────────────
  // Supported: ?new=1, ?import=1, ?filter=high-risk|upcoming|needs-attention
  //            ?open=<itemId>  (from audit trail row click — opens HistoryDrawer)
  useEffect(() => {
    if (searchParams.get("new") === "1") {
      if (canEdit) handleCreate();
      setSearchParams({}, { replace: true });
    } else if (searchParams.get("import") === "1") {
      if (canEdit) setIsImportOpen(true);
      setSearchParams({}, { replace: true });
    } else if (searchParams.get("filter")) {
      const f = searchParams.get("filter") as CardFilter;
      const validFilters: CardFilter[] = [
        "high-risk",
        "upcoming",
        "needs-attention",
      ];
      if (validFilters.includes(f)) setActiveFilter(f);
      setSearchParams({}, { replace: true });
    } else if (searchParams.get("open")) {
      const itemId = searchParams.get("open");
      setSearchParams({}, { replace: true });
      if (itemId) setPendingOpenId(itemId);
    }
    // handleCreate and openDrawer are stable callbacks, safe to list:
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, setSearchParams, items]);

  // ── Open HistoryDrawer for a pending ?open= item ID once items load ────────
  useEffect(() => {
    if (!pendingOpenId || items.length === 0) return;
    const target = items.find((i) => i.id === pendingOpenId);
    if (target) {
      openDrawer(target);
      setPendingOpenId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingOpenId, items]);

  // ── Derived data ───────────────────────────────────────────────────────────
  const summaryCards = useMemo(() => buildSummaryCards(items), [items]);
  const filteredItems = useMemo(
    () => filterBySearch(filterByCard(items, activeFilter), searchQuery),
    [items, activeFilter, searchQuery]
  );

  // ── Column definitions (shared hook) ──────────────────────────────────────
  const columns = useExpiryColumns({
    onAddFields: canEdit ? handleAddFields : undefined,
    onRenew: canEdit ? handleRenewItem : undefined,
    onSendReminder: handleSendReminder,
    onRowClick: openDrawer,
  });

  // ── Card filter label lookup ───────────────────────────────────────────────
  const filterLabel: Record<NonNullable<CardFilter>, string> = {
    "high-risk": "High Risk",
    upcoming: "Upcoming",
    "needs-attention": "Needs Attention",
  };

  // ── Loading state ──────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <>
        <Header onSettings={() => setIsSettingsOpen(true)} onOpenPricing={() => navigate("/pricing")} />
        <div className="bg-[#F9FAFB] min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#2563EB]" />
        </div>
      </>
    );
  }

  // ── New-user detection ─────────────────────────────────────────────────────
  // Show the welcome experience only when: no real items AND user hasn't dismissed it.
  const isNewUser = !isLoading && items.length === 0 && !welcomeDismissed;

  // ── Main render ────────────────────────────────────────────────────────────
  return (
    <>
      <Header
        onAddExpiry={handleCreate}
        onImport={() => setIsImportOpen(true)}
        onSettings={() => setIsSettingsOpen(true)}
        onOpenPricing={() => navigate("/pricing")}
      />

      <div className="bg-[#F9FAFB] min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {isNewUser ? (
            /* ── New-user onboarding experience ──────────────────────────── */
            <NewUserWelcome
              userName={user?.name || user?.first_name}
              userEmail={user?.email}
              onAddDocument={handleCreate}
              onImport={() => setIsImportOpen(true)}
              onDismiss={dismissWelcome}
            />
          ) : (
            /* ── Normal view: summary cards + search + table ─────────────── */
            <>
              {/* ── Summary Cards (clickable to filter) ─────────────────── */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                {summaryCards.map((card) => (
                  <SummaryCard
                    key={card.variant}
                    data={card}
                    isActive={activeFilter === card.filter}
                    onClick={() =>
                      setActiveFilter((prev) =>
                        prev === card.filter ? null : (card.filter ?? null)
                      )
                    }
                  />
                ))}
              </div>

              {/* ── Search bar + active-filter chip ─────────────────────── */}
              <div className="bg-white rounded-t-lg border border-[#E5E7EB] shadow-sm">
                <div className="flex items-center px-4 py-3 space-x-3">
                  {activeFilter && (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#EFF6FF] border border-[#BFDBFE] text-[#1D4ED8] text-xs font-medium rounded-full">
                      {activeFilter === "high-risk" && "🔴"}
                      {activeFilter === "upcoming" && "🟡"}
                      {activeFilter === "needs-attention" && "⚠️"}
                      {filterLabel[activeFilter]}
                      <button
                        onClick={() => setActiveFilter(null)}
                        className="ml-0.5 text-[#3B82F6] hover:text-[#1D4ED8] transition-colors"
                        aria-label="Clear filter"
                      >
                        ✕
                      </button>
                    </span>
                  )}

                  <div className="flex-1">
                    <SearchBar
                      value={searchQuery}
                      onChange={setSearchQuery}
                      placeholder="Search documents…"
                    />
                  </div>
                </div>
              </div>

              {/* ── Data Table ──────────────────────────────────────────── */}
              <div className="bg-white rounded-b-lg border border-t-0 border-[#E5E7EB] shadow-sm overflow-hidden">
                <DataTable
                  columns={columns}
                  data={filteredItems}
                  onRowSelect={canEdit ? handleRowSelect : undefined}
                  selectedRows={selectedRows}
                  getRowId={(row) => row.id}
                  onRowAction={canEdit ? handleRowAction : undefined}
                  emptyMessage={
                    activeFilter
                      ? `No items match the "${filterLabel[activeFilter]}" filter.`
                      : "No documents found. Create your first document!"
                  }
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Bulk Action Bar ────────────────────────────────────────────────── */}
      <BulkActionBar
        selectedCount={selectedRows.size}
        onClear={handleClearSelection}
        onRenew={handleBulkRenew}
        onEditExpiry={handleBulkEditExpiry}
        onAssignOwner={handleBulkAssignOwner}
        onChangeCategory={handleBulkChangeCategory}
        onSnooze={handleBulkSnooze}
        onArchive={handleBulkArchive}
        onExport={handleBulkExport}
        visible={selectedRows.size > 0}
      />

      {/* ── Item Modal ─────────────────────────────────────────────────────── */}
      {isModalOpen && (
        <ItemModal
          item={editingItem}
          onClose={closeModal}
          onSubmit={handleSubmit}
          initialRenewalMode={modalRenewalMode}
          focusExpiryDate={modalFocusExpiry}
        />
      )}

      {/* ── Import Modal ───────────────────────────────────────────────────── */}
      {isImportOpen && (
        <ImportModal
          onClose={() => setIsImportOpen(false)}
          onImport={async (csvText) => {
            await handleImport(csvText);
            setIsImportOpen(false);
          }}
        />
      )}

      {/* ── Settings Modal ─────────────────────────────────────────────────── */}
      {isSettingsOpen && (
        <SettingsModal onClose={() => setIsSettingsOpen(false)} />
      )}

      {/* ── History Drawer ──────────────────────────────────────────────────── */}
      <HistoryDrawer
        item={drawerItem}
        onClose={closeDrawer}
        onEdit={
          canEdit
            ? (item) => {
                closeDrawer();
                handleEdit(item);
              }
            : undefined
        }
        onRenew={
          canEdit
            ? (item) => {
                closeDrawer();
                handleRenewItem(item);
              }
            : undefined
        }
        onSendReminder={handleSendReminder}
        onArchive={
          canEdit ? (item) => handleRowAction(item, "archive") : undefined
        }
      />
    </>
  );
};

export default ExpiryItems;
